import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  UserIcon,
  BriefcaseIcon,
  HeartIcon,
  BellIcon,
  CogIcon,
  MapPinIcon,
  CurrencyYenIcon,
  CalendarIcon,
  StarIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  IdentificationIcon,
  WalletIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  MapIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon,
  LifebuoyIcon,
  QuestionMarkCircleIcon,
  ExclamationCircleIcon,
  BuildingLibraryIcon,
  DocumentIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

const safeText = (v) => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch (_) { return String(v); }
};

const MyPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [missions, setMissions] = useState(null);
  const [identityStatus, setIdentityStatus] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.user_type !== 'job_seeker') {
      navigate('/');
      return;
    }

    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found in localStorage');
        return;
      }

      const authApi = apiWithAuth(token);

      console.log('Fetching data with token:', !!token);

      const [applicationsRes, notificationsRes, missionsRes, identityRes, walletRes, badgesRes] = await Promise.all([
        // Use real endpoints across environments
        authApi.get('/applications').catch(err => ({ data: [] })),
        authApi.get('/notifications').catch(err => ({ data: [] })),
        authApi.get('/user/missions').catch(err => ({ data: { registration_completed: true, identity_verified: false, first_job_completed: false } })),
        authApi.get('/identity-verification/status').catch(err => ({ data: null })),
        // Wallet endpoints exist under /api/wallet*
        authApi.get('/wallet').catch(err => ({ data: { balance: 0 } })),
        authApi.get('/user/badges').catch(err => ({ data: [] })),
      ]);

      setApplications(applicationsRes?.data || []);
      setNotifications(notificationsRes?.data || []);

      const badges = Array.isArray(badgesRes?.data) ? badgesRes.data : [];
      const byId = {};
      badges.forEach(b => { if (b && b.id) byId[b.id] = b; });

      const mergedMissions = {
        ...(missionsRes?.data || {}),
        registration_completed: byId['registration-completed'] ? !!byId['registration-completed'].earned : (missionsRes?.data?.registration_completed ?? false),
        identity_verified: byId['identity-verified'] ? !!byId['identity-verified'].earned : (missionsRes?.data?.identity_verified ?? false),
        first_job_completed: byId['first-job'] ? !!byId['first-job'].earned : (missionsRes?.data?.first_job_completed ?? false),
      };
      setMissions(mergedMissions);
      setIdentityStatus(identityRes?.data);

      // Safety check for wallet data
      if (walletRes && walletRes.data) {
        setWallet(walletRes.data);
      } else {
        console.warn('Wallet data missing or invalid, setting default');
        setWallet({ balance: 0 });
      }

      console.log('Data fetched successfully:', {
        applications: applicationsRes?.data?.length,
        notifications: notificationsRes?.data?.length,
        missions: missionsRes?.data,
        wallet: walletRes?.data
      });
    } catch (error) {
      console.error('Failed to fetch data completely:', error);
      // Fallback defaults
      setMissions({ registration_completed: true, identity_verified: false, first_job_completed: false });
      setWallet({ balance: 0 });
      toast.error('一部のデータの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('ログアウトしました');
  };

  const userId = (user && (user.id || user._id || user.user_id || user.email || '')) || '';

  const copyUserId = async () => {
    try {
      await navigator.clipboard.writeText(String(userId));
      toast.success('ユーザーIDをコピーしました');
    } catch (_) {
      toast.error('コピーに失敗しました');
    }
  };

  // メニューセクションの定義
  const menuSections = [
    {
      title: 'アカウント',
      items: [
        {
          id: 'account-settings',
          label: 'アカウント設定',
          icon: CogIcon,
          path: '/mypage/account-settings'
        },
        {
          id: 'reviews-penalties',
          label: 'レビューとペナルティ',
          icon: StarIcon,
          path: '/mypage/reviews-penalties'
        },
        {
          id: 'badges',
          label: 'バッジ一覧',
          icon: TrophyIcon,
          path: '/mypage/badges'
        },
        {
          id: 'qualifications',
          label: '保有資格',
          icon: AcademicCapIcon,
          path: '/mypage/qualifications'
        }
      ]
    },
    {
      title: 'お仕事',
      items: [
        {
          id: 'work-history',
          label: 'これまでの仕事',
          icon: ClipboardDocumentListIcon,
          path: '/mypage/work-history'
        },
        {
          id: 'earnings',
          label: '獲得した報酬',
          icon: ChartBarIcon,
          path: '/mypage/earnings'
        },
        {
          id: 'tax-documents',
          label: '源泉徴収票の確認と印刷',
          icon: DocumentTextIcon,
          path: '/mypage/tax-documents'
        }
      ]
    },
    {
      title: '通知とプライバシー',
      items: [
        {
          id: 'location-settings',
          label: '位置情報の設定',
          icon: MapIcon,
          path: '/mypage/location-settings'
        },
        {
          id: 'push-notifications',
          label: 'プッシュ通知設定',
          icon: BellIcon,
          path: '/mypage/push-notifications'
        },
        {
          id: 'muted-stores',
          label: 'ミュートしている店舗',
          icon: XMarkIcon,
          path: '/mypage/muted-stores'
        },
        {
          id: 'email-settings',
          label: 'お知らせメール設定',
          icon: ChatBubbleLeftEllipsisIcon,
          path: '/mypage/email-settings'
        },
        {
          id: 'health-check',
          label: '検便依頼と結果',
          icon: ShieldCheckIcon,
          path: '/mypage/health-check'
        }
      ]
    },
    {
      title: 'サポート',
      items: [
        {
          id: 'contact',
          label: 'お問い合わせ',
          icon: LifebuoyIcon,
          path: '/mypage/contact'
        },
        {
          id: 'faq',
          label: 'よくある質問',
          icon: QuestionMarkCircleIcon,
          path: '/mypage/faq'
        },
        {
          id: 'bug-report',
          label: 'アプリの不具合・改善要望を報告',
          icon: ExclamationCircleIcon,
          path: '/mypage/bug-report'
        }
      ]
    },
    {
      title: 'その他',
      items: [
        {
          id: 'privacy-policy',
          label: 'プライバシーポリシー',
          icon: ShieldCheckIcon,
          path: '/mypage/privacy-policy'
        },
        {
          id: 'terms',
          label: '利用規約',
          icon: DocumentIcon,
          path: '/mypage/terms'
        },
        {
          id: 'job-categories',
          label: '取扱職種の範囲',
          icon: BuildingLibraryIcon,
          path: '/mypage/job-categories'
        },
        {
          id: 'join-team',
          label: 'バイターズを一緒につくりませんか？',
          icon: UserGroupIcon,
          path: '/mypage/join-team'
        },
        {
          id: 'withdrawal',
          label: '退会手続き',
          icon: ArrowRightOnRectangleIcon,
          path: '/mypage/withdrawal'
        }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">マイページ</h1>
            </div>
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 font-medium"
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* User Profile Card */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-gray-300 rounded-full mx-auto mb-4 flex items-center justify-center">
                  {user.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt="プロフィール"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-8 h-8 text-gray-500" />
                  )}
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{safeText(user.name)}</h2>
                <p className="text-sm text-gray-600">{safeText(user.email)}</p>
                <div className="mt-2 flex items-center justify-center gap-2">
                  <span className="text-xs text-gray-500 break-all">ID: {safeText(userId || '未取得')}</span>
                  <button
                    onClick={copyUserId}
                    className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700"
                  >
                    IDコピー
                  </button>
                </div>

                {/* Wallet Balance - 常に表示 */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-4 text-white mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-90">現在のウォレット残高</p>
                        <p className="text-2xl font-bold">{wallet ? wallet.balance.toLocaleString() : '0'}</p>
                      </div>
                      <WalletIcon className="w-8 h-8 opacity-80" />
                    </div>

                    {/* ウォレット機能ボタン4つ */}
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <button
                        onClick={() => navigate('/wallet-management?tab=withdrawals')}
                        className="bg-white bg-opacity-20 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-opacity-30 transition-colors flex items-center justify-center"
                      >
                        <ArrowDownTrayIcon className="w-3 h-3 mr-1" />
                        引き出す
                      </button>
                      <button
                        onClick={() => navigate('/wallet-management?tab=bank-accounts')}
                        className="bg-white bg-opacity-20 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-opacity-30 transition-colors flex items-center justify-center"
                      >
                        <CreditCardIcon className="w-3 h-3 mr-1" />
                        口座編集
                      </button>
                      <button
                        onClick={() => navigate('/wallet-management?tab=transactions')}
                        className="bg-white bg-opacity-20 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-opacity-30 transition-colors flex items-center justify-center"
                      >
                        <ClockIcon className="w-3 h-3 mr-1" />
                        入出金履歴
                      </button>
                      <button
                        onClick={() => navigate('/wallet-management')}
                        className="bg-white bg-opacity-20 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-opacity-30 transition-colors flex items-center justify-center"
                      >
                        <WalletIcon className="w-3 h-3 mr-1" />
                        報酬管理
                      </button>
                    </div>
                  </div>
                </div>

                {/* User Stats & Missions */}
                {missions && (
                  <div className="mt-4">
                    {/* Missions Progress */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">あなたの実績</h3>

                      {/* Mission Icons */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-1 ${missions.registration_completed ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                            {missions.registration_completed ? (
                              <CheckCircleIconSolid className="w-6 h-6 text-green-600" />
                            ) : (
                              <ClockIcon className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600">登録完了</p>
                        </div>

                        <div className="text-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-1 ${missions.identity_verified ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                            {missions.identity_verified ? (
                              <CheckCircleIconSolid className="w-6 h-6 text-blue-600" />
                            ) : (
                              <IdentificationIcon className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600">本人確認</p>
                        </div>

                        <div className="text-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-1 ${missions.first_job_completed ? 'bg-yellow-100' : 'bg-gray-100'
                            }`}>
                            {missions.first_job_completed ? (
                              <TrophyIcon className="w-6 h-6 text-yellow-600" />
                            ) : (
                              <BriefcaseIcon className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600">初回の仕事</p>
                        </div>
                      </div>

                      {/* Identity Verification Button */}
                      {(!identityStatus || identityStatus.status === 'not_started' || identityStatus.status === 'rejected') && (
                        <button
                          onClick={() => navigate('/identity-verification')}
                          className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                        >
                          <IdentificationIcon className="w-4 h-4 mr-2" />
                          本人確認書類提出
                        </button>
                      )}

                      {/* Identity Verification Status */}
                      {identityStatus && identityStatus.status !== 'not_started' && (
                        <div className="mt-4 p-3 rounded-lg border">
                          {identityStatus.status === 'in_progress' && (
                            <div className="flex items-center text-yellow-600">
                              <ClockIcon className="w-4 h-4 mr-2" />
                              <span className="text-sm">本人確認：確認中</span>
                            </div>
                          )}
                          {identityStatus.status === 'completed' && (
                            <div className="flex items-center text-green-600">
                              <CheckCircleIconSolid className="w-4 h-4 mr-2" />
                              <span className="text-sm">本人確認：承認済</span>
                            </div>
                          )}
                          {identityStatus.status === 'rejected' && (
                            <div className="text-red-600">
                              <div className="flex items-center mb-1">
                                <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                                <span className="text-sm">本人確認：再提出が必要</span>
                              </div>
                              {identityStatus.admin_comment && (
                                <p className="text-xs text-red-500">{identityStatus.admin_comment}</p>
                              )}
                              <button
                                onClick={() => navigate('/identity-verification')}
                                className="w-full mt-2 bg-red-600 text-white py-1 px-3 rounded text-xs hover:bg-red-700 transition-colors"
                              >
                                再提出する
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* User Stats - 常に表示 */}
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">{missions?.completed_jobs_count || 0}</div>
                    <div className="text-xs text-gray-500">完了した仕事</div>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center">
                      <StarIcon className="w-4 h-4 text-yellow-400 mr-1" />
                      <span className="text-lg font-bold text-yellow-600">
                        {Number.isFinite(Number(missions?.average_rating)) ? Number(missions.average_rating).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">評価</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">クイックアクション</h3>
              <div className="space-y-3">
                <Link
                  to="/profile"
                  className="flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <UserIcon className="w-5 h-5 mr-3 text-gray-400" />
                    <span className="text-gray-900">プロフィール編集</span>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </Link>
                <Link
                  to="/applications"
                  className="flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <BriefcaseIcon className="w-5 h-5 mr-3 text-gray-400" />
                    <span className="text-gray-900">応募履歴</span>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </Link>
              </div>
            </div>
          </div>

          {/* Menu Sections */}
          <div className="flex-1">
            <div className="space-y-6">
              {menuSections.map((section) => (
                <div key={section.title} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {section.items.map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <Link
                          key={item.id}
                          to={item.path}
                          className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center">
                            <IconComponent className="w-5 h-5 mr-3 text-gray-400" />
                            <span className="text-gray-900">{item.label}</span>
                          </div>
                          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPage;